import styled from '@emotion/styled';
import React from 'react';
import { editorAccent } from './editor-accent';
import { theme } from 'styles/theme/theme';

export interface EditorLayoutProps {
  header: React.ReactNode;
  left: React.ReactNode;
  right: React.ReactNode;
  canvas: React.ReactNode;
  /**
   * Слой поверх канвы — скелетон на время загрузки или ветка ошибки. Непрозрачный и
   * перехватывает указатель: пока объекты шаблона не легли, править пустую канву нельзя.
   * Канва под ним остаётся смонтированной — без неё не появится `editor`.
   */
  canvasOverlay?: React.ReactNode;
}

const EditorLayout: React.FC<EditorLayoutProps> = ({ header, left, right, canvas, canvasOverlay }) => {
  return (
    <Root>
      <HeaderSlot>{header}</HeaderSlot>
      <Body>
        <SideSlot>{left}</SideSlot>
        <CanvasSlot>
          {canvas}
          {canvasOverlay ? <CanvasOverlay data-testid="editor-canvas-overlay">{canvasOverlay}</CanvasOverlay> : null}
        </CanvasSlot>
        <SideSlot>{right}</SideSlot>
      </Body>
    </Root>
  );
};

export default EditorLayout;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  overflow: hidden;

  /* Native controls (inspector checkboxes, the layout-block opacity slider)
     default to the browser's blue — repaint them with the product accent. */
  input[type='checkbox'],
  input[type='radio'],
  input[type='range'] {
    accent-color: ${editorAccent.solid};
  }
`;

const HeaderSlot = styled.div`
  flex: 0 0 auto;
`;

const Body = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
`;

const SideSlot = styled.aside`
  flex: 0 0 auto;
`;

const CanvasSlot = styled.main`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  background: ${theme.colors.SurfaceCultured10};
`;

// `absolute`, а не в потоке: канва меряет свой размер от слота, и оверлей не должен его менять.
// Фон — тот же, что у слота: сквозь слой не видно пустой канвы.
const CanvasOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.SurfaceCultured10};
`;
